(function() {
    'use strict';

    const win = window;
    const doc = document;
    const config = win.coeUndergradDegreeAnalyticsData || {};

    if (!doc || !doc.addEventListener) {
        return;
    }

    const EVENTS = {
        sectionView: 'undergrad_degree_section_view',
        interaction: 'undergrad_degree_interaction',
        linkClick: 'undergrad_degree_link_click',
        video: 'undergrad_degree_video',
        scroll: 'undergrad_degree_scroll',
    };

    const SECTION_DEFAULTS = {
        sample_degree_plan: 'Sample Degree/Major Plan',
        certificate_programs: 'Certificate programs',
        accelerated_programs: 'Accelerated programs',
        scholarships: 'Scholarships',
        admissions: 'Admissions',
        built_in_network: 'Your built-in network',
        departments: 'Departments',
        life_as_badger_engineer: 'Life as a Badger engineer',
        abet: 'ABET accreditation',
        compare_majors: 'Compare majors',
        contact: 'Contact',
    };

    const SECTION_SELECTORS = [
        ['.wp-block-acf-four-year-timeline-26', 'sample_degree_plan'],
        ['.wp-block-acf-programs-related-26', 'certificate_programs'],
        ['.wp-block-acf-image-accordion-26', 'built_in_network'],
        ['.wp-block-acf-content-showcase-26', 'life_as_badger_engineer'],
        ['.wp-block-acf-program-abet', 'abet'],
        ['.wp-block-acf-program-comparison-selector', 'compare_majors'],
    ];

    const queuedEvents = [];
    const sectionViewsTracked = new Set();
    const scrollThresholdsTracked = new Set();
    const videoPlayers = new WeakMap();
    let waitingForDataLayer = false;
    let dynamicObserver = null;

    function baseContext() {
        return {
            page_type: 'undergraduate_degree',
            content_group: 'undergraduate_degree',
            program_id: config.program_id || '',
            program_title: config.program_title || '',
            program_slug: config.program_slug || '',
        };
    }

    function cleanPayload(payload) {
        return Object.keys(payload).reduce((cleaned, key) => {
            const value = payload[key];
            if (value !== undefined && value !== null && value !== '') {
                cleaned[key] = value;
            }
            return cleaned;
        }, {});
    }

    function waitForDataLayer(callback) {
        if (Array.isArray(win.dataLayer)) {
            callback();
            return;
        }

        win.setTimeout(() => waitForDataLayer(callback), 100);
    }

    function flushQueuedEvents() {
        if (!Array.isArray(win.dataLayer)) {
            return;
        }

        while (queuedEvents.length) {
            win.dataLayer.push(queuedEvents.shift());
        }
    }

    function push(eventName, params = {}) {
        const payload = cleanPayload({
            event: eventName,
            ...baseContext(),
            ...params,
        });

        if (Array.isArray(win.dataLayer)) {
            win.dataLayer.push(payload);
            return payload;
        }

        queuedEvents.push(payload);
        if (!waitingForDataLayer) {
            waitingForDataLayer = true;
            waitForDataLayer(() => {
                waitingForDataLayer = false;
                flushQueuedEvents();
            });
        }

        return payload;
    }

    function normalizeText(text) {
        return String(text || '').replace(/\s+/g, ' ').trim();
    }

    function stripAssistiveText(text) {
        return normalizeText(text).replace(/\(opens in new window\)/ig, '').trim();
    }

    function getLinkText(link) {
        if (typeof link.cloneNode !== 'function') {
            return stripAssistiveText(link.textContent || '');
        }

        const clone = link.cloneNode(true);
        if (clone.querySelectorAll) {
            clone.querySelectorAll('.visually-hidden, [aria-hidden="true"]').forEach((node) => {
                node.remove();
            });
        }

        return stripAssistiveText(clone.textContent || link.textContent || '');
    }

    function getAbsoluteUrl(url) {
        try {
            return new URL(url, win.location.href);
        } catch (error) {
            return null;
        }
    }

    function getSectionTitle(element, sectionName) {
        if (!element) {
            return SECTION_DEFAULTS[sectionName] || '';
        }

        if (element.dataset && element.dataset.analyticsSectionTitle) {
            return normalizeText(element.dataset.analyticsSectionTitle);
        }

        const heading = element.querySelector ? element.querySelector('h1, h2, h3, h4, .section-title, .program-comparison-selector-title') : null;
        return normalizeText(heading ? heading.textContent : SECTION_DEFAULTS[sectionName]);
    }

    function getExplicitSectionData(element) {
        const sectionElement = element && element.closest ? element.closest('[data-analytics-section]') : null;
        if (!sectionElement || !sectionElement.dataset.analyticsSection) {
            return {};
        }

        return {
            content_section: sectionElement.dataset.analyticsSection,
            section_title: getSectionTitle(sectionElement, sectionElement.dataset.analyticsSection),
        };
    }

    function getKnownSectionData(element) {
        if (!element || !element.closest) {
            return {};
        }

        for (const [selector, sectionName] of SECTION_SELECTORS) {
            const sectionElement = element.closest(selector);
            if (sectionElement) {
                return {
                    content_section: sectionName,
                    section_title: getSectionTitle(sectionElement, sectionName),
                };
            }
        }

        return {};
    }

    function getSectionData(element) {
        return {
            ...getKnownSectionData(element),
            ...getExplicitSectionData(element),
        };
    }

    function getAdmissionType(url, linkText = '') {
        const haystack = `${url.href} ${linkText}`.toLowerCase();

        if (haystack.includes('cross-campus')) {
            return 'cross_campus';
        }

        if (haystack.includes('reentry')) {
            return 'reentry';
        }

        if (haystack.includes('transfer')) {
            return 'transfer';
        }

        if (haystack.includes('freshman') || haystack.includes('first-year') || haystack.includes('first year')) {
            return 'freshman';
        }

        return '';
    }

    function getItemTitleFromContext(link) {
        const card = link && link.closest ? link.closest('.card, .featured-post-card, .program-selector-item, .swiper-slide') : null;
        const title = card && card.querySelector ? card.querySelector('h3, h4, h5, .card-title, .program-name, .featured-title') : null;

        return normalizeText(title ? title.textContent : getLinkText(link));
    }

    function getActiveAccordionTitle(link) {
        const block = link && link.closest ? link.closest('.wp-block-acf-image-accordion-26') : null;
        if (!block || !block.querySelector) {
            return '';
        }

        const activeButton = block.querySelector('.accordion-button[aria-expanded="true"]');
        return normalizeText(activeButton ? activeButton.textContent : '');
    }

    function readExplicitLinkData(link) {
        const dataset = link.dataset || {};
        const sectionElement = link.closest ? link.closest('[data-analytics-section]') : null;
        const sectionDataset = sectionElement ? sectionElement.dataset : {};

        return cleanPayload({
            engagement_type: dataset.analyticsEngagementType,
            content_section: dataset.analyticsContentSection || sectionDataset.analyticsSection,
            section_title: dataset.analyticsSectionTitle || sectionDataset.analyticsSectionTitle,
            link_destination: dataset.analyticsLinkDestination,
            admission_type: dataset.analyticsAdmissionType,
            contact_type: dataset.analyticsContactType,
            item_title: dataset.analyticsItemTitle,
            item_position: dataset.analyticsItemPosition,
        });
    }

    function inferLinkData(link) {
        const url = getAbsoluteUrl(link.href);
        const linkText = getLinkText(link);

        if (!url) {
            return {};
        }

        const href = url.href.toLowerCase();
        const text = linkText.toLowerCase();

        if (href.startsWith('mailto:')) {
            return {
                engagement_type: 'email_click',
                content_section: 'contact',
                section_title: SECTION_DEFAULTS.contact,
                link_destination: 'email',
                contact_type: 'email',
            };
        }

        const admissionType = getAdmissionType(url, linkText);
        if (admissionType) {
            return {
                engagement_type: 'admission_link_click',
                content_section: 'admissions',
                section_title: SECTION_DEFAULTS.admissions,
                link_destination: 'admissions',
                admission_type: admissionType,
            };
        }

        if (href.includes('scholarship') || text.includes('scholarship')) {
            return {
                engagement_type: 'scholarship_link_click',
                content_section: 'scholarships',
                section_title: SECTION_DEFAULTS.scholarships,
                link_destination: 'scholarships',
            };
        }

        if (href.includes('accelerated') || href.includes('_learning_mode=accelerated')) {
            return {
                engagement_type: 'accelerated_programs_click',
                content_section: 'accelerated_programs',
                section_title: SECTION_DEFAULTS.accelerated_programs,
                link_destination: 'accelerated_programs',
            };
        }

        if (href.includes('/departments/')) {
            return {
                engagement_type: 'department_link_click',
                content_section: 'departments',
                section_title: SECTION_DEFAULTS.departments,
                link_destination: 'department',
            };
        }

        if (link.closest && link.closest('.wp-block-acf-programs-related-26')) {
            return {
                engagement_type: 'certificate_link_click',
                content_section: 'certificate_programs',
                section_title: SECTION_DEFAULTS.certificate_programs,
                link_destination: 'certificate_program',
                item_title: getItemTitleFromContext(link),
            };
        }

        if (link.closest && link.closest('.wp-block-acf-image-accordion-26')) {
            return {
                engagement_type: 'built_in_network_link_click',
                content_section: 'built_in_network',
                section_title: getActiveAccordionTitle(link) || SECTION_DEFAULTS.built_in_network,
                link_destination: 'built_in_network',
            };
        }

        if (link.closest && link.closest('.wp-block-acf-content-showcase-26')) {
            return {
                engagement_type: 'story_link_click',
                content_section: 'life_as_badger_engineer',
                section_title: SECTION_DEFAULTS.life_as_badger_engineer,
                link_destination: 'story',
                item_title: getItemTitleFromContext(link),
            };
        }

        return {};
    }

    function classifyLink(link) {
        const explicitData = readExplicitLinkData(link);
        const inferredData = inferLinkData(link);
        const sectionData = explicitData.content_section ? {} : getSectionData(link);
        const linkText = getLinkText(link);
        const url = getAbsoluteUrl(link.href);

        const payload = cleanPayload({
            engagement_type: 'link_click',
            ...sectionData,
            ...inferredData,
            ...explicitData,
            link_text: linkText,
            link_url: url ? url.href : link.href,
        });

        if (!payload.content_section && !payload.link_destination && !payload.contact_type) {
            return null;
        }

        return payload;
    }

    function applySectionData(element, sectionName, sectionTitle) {
        if (!element || !element.dataset || element.dataset.analyticsSection) {
            return;
        }

        element.dataset.analyticsSection = sectionName;
        element.dataset.analyticsSectionTitle = sectionTitle || SECTION_DEFAULTS[sectionName] || '';
    }

    function markInferredSections() {
        SECTION_SELECTORS.forEach(([selector, sectionName]) => {
            doc.querySelectorAll(selector).forEach((element) => {
                applySectionData(element, sectionName, getSectionTitle(element, sectionName));
            });
        });

        // Legacy Callout Slim Dark until every page has moved to Callout Bar.
        doc.querySelectorAll('.wp-block-acf-callout-slim-dark, .wp-block-coe-callout-bar.is-scheme-dark').forEach((block) => {
            const links = Array.from(block.querySelectorAll('a[href]'));
            const linkData = links.map(inferLinkData).find((data) => data.content_section === 'accelerated_programs' || data.content_section === 'departments');
            if (linkData) {
                applySectionData(block, linkData.content_section, linkData.section_title);
            }
        });

        doc.querySelectorAll('.wp-block-acf-stats-26').forEach((block) => {
            const text = normalizeText(block.textContent).toLowerCase();
            const hasScholarshipLink = Array.from(block.querySelectorAll('a[href]')).some((link) => {
                const data = inferLinkData(link);
                return data.content_section === 'scholarships';
            });

            if (hasScholarshipLink || text.includes('scholarship')) {
                applySectionData(block, 'scholarships', SECTION_DEFAULTS.scholarships);
            }
        });

        doc.querySelectorAll('a[href]').forEach((link) => {
            const linkData = inferLinkData(link);
            if (!linkData.content_section) {
                return;
            }

            if (linkData.content_section === 'admissions') {
                const root = link.closest('.wp-block-group, .wp-block-columns, .wp-block-column, .wp-block-acf-section-container-26, section, .card') || link;
                applySectionData(root, 'admissions', SECTION_DEFAULTS.admissions);
            }

            if (linkData.content_section === 'contact') {
                const root = link.closest('.wp-block-acf-card-horizontal-26, .wp-block-group, .card, section') || link;
                applySectionData(root, 'contact', SECTION_DEFAULTS.contact);
            }
        });
    }

    function trackSectionView(element) {
        const sectionData = getSectionData(element);
        if (!sectionData.content_section || sectionViewsTracked.has(sectionData.content_section)) {
            return;
        }

        sectionViewsTracked.add(sectionData.content_section);
        push(EVENTS.sectionView, {
            engagement_type: 'section_view',
            ...sectionData,
        });
    }

    function elementHasMeaningfulVisibility(element) {
        const rect = element.getBoundingClientRect();
        const viewportHeight = win.innerHeight || doc.documentElement.clientHeight;

        if (rect.height === 0 || rect.width === 0) {
            return false;
        }

        const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
        return visibleHeight >= Math.min(rect.height * 0.4, 240);
    }

    function initSectionViews() {
        const sections = Array.from(doc.querySelectorAll('[data-analytics-section]'));

        if ('IntersectionObserver' in win) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
                        trackSectionView(entry.target);
                        observer.unobserve(entry.target);
                    }
                });
            }, {
                threshold: [0.4],
            });

            sections.forEach((section) => observer.observe(section));
            return;
        }

        const checkFallbackViews = () => {
            sections.forEach((section) => {
                if (elementHasMeaningfulVisibility(section)) {
                    trackSectionView(section);
                }
            });
        };

        win.addEventListener('scroll', checkFallbackViews, { passive: true });
        checkFallbackViews();
    }

    function initScrollTracking() {
        const thresholds = [25, 50, 75, 90, 100];

        function checkScroll() {
            const docEl = doc.documentElement;
            const scrollableHeight = Math.max(docEl.scrollHeight, doc.body.scrollHeight) - win.innerHeight;
            if (scrollableHeight <= 0) {
                return;
            }

            const scrollPercent = (win.scrollY / scrollableHeight) * 100;
            thresholds.forEach((threshold) => {
                if (scrollPercent >= threshold && !scrollThresholdsTracked.has(threshold)) {
                    scrollThresholdsTracked.add(threshold);
                    push(EVENTS.scroll, {
                        percent_scrolled: threshold,
                    });
                }
            });
        }

        win.addEventListener('scroll', checkScroll, { passive: true });
        checkScroll();
    }

    function getVideoMetadata(videoElement) {
        const sectionData = getSectionData(videoElement);
        const src = videoElement.getAttribute('src') || '';
        const embedMatch = src.match(/embed\/([^?&]+)/);
        const videoId = videoElement.getAttribute('videoid') || videoElement.dataset.videoid || (embedMatch ? embedMatch[1] : '');
        const caption = videoElement.closest && videoElement.closest('.video-wrapper')
            ? videoElement.closest('.video-wrapper').querySelector('.video-caption')
            : null;

        return cleanPayload({
            content_section: sectionData.content_section || 'life_as_badger_engineer',
            section_title: sectionData.section_title || SECTION_DEFAULTS.life_as_badger_engineer,
            item_title: normalizeText(caption ? caption.textContent : videoId),
            link_url: videoId ? `https://www.youtube.com/watch?v=${videoId}` : '',
        });
    }

    function trackVideo(videoElement, engagementType, extra = {}) {
        push(EVENTS.video, {
            ...getVideoMetadata(videoElement),
            engagement_type: engagementType,
            ...extra,
        });
    }

    function watchVideoProgress(videoElement, player, state) {
        if (state.progressTimer) {
            return;
        }

        state.progressTimer = win.setInterval(() => {
            if (!player || typeof player.getCurrentTime !== 'function' || typeof player.getDuration !== 'function') {
                return;
            }

            const duration = player.getDuration();
            if (!duration) {
                return;
            }

            const progress = (player.getCurrentTime() / duration) * 100;
            [25, 50, 75].forEach((milestone) => {
                if (progress >= milestone && !state.milestones.has(milestone)) {
                    state.milestones.add(milestone);
                    trackVideo(videoElement, 'video_progress', {
                        video_progress: milestone,
                    });
                }
            });
        }, 1000);
    }

    function initYouTubePlayers() {
        if (!win.YT || typeof win.YT.Player !== 'function') {
            return;
        }

        doc.querySelectorAll('iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"]').forEach((iframe) => {
            if (videoPlayers.has(iframe)) {
                return;
            }

            const state = {
                playTracked: false,
                milestones: new Set(),
                progressTimer: null,
            };

            try {
                const player = new win.YT.Player(iframe, {
                    events: {
                        onStateChange(event) {
                            if (event.data === win.YT.PlayerState.PLAYING) {
                                if (!state.playTracked) {
                                    state.playTracked = true;
                                    trackVideo(iframe, 'video_play');
                                }
                                watchVideoProgress(iframe, player, state);
                            }

                            if (event.data === win.YT.PlayerState.PAUSED) {
                                let progress = '';
                                if (typeof event.target.getCurrentTime === 'function' && typeof event.target.getDuration === 'function' && event.target.getDuration()) {
                                    progress = Math.round((event.target.getCurrentTime() / event.target.getDuration()) * 100);
                                }

                                trackVideo(iframe, 'video_pause', {
                                    video_progress: progress,
                                });
                            }

                            if (event.data === win.YT.PlayerState.ENDED) {
                                state.milestones.add(100);
                                trackVideo(iframe, 'video_complete', {
                                    video_progress: 100,
                                });
                            }
                        },
                    },
                });

                videoPlayers.set(iframe, state);
            } catch (error) {
                // Ignore iframe API setup failures; video_open click tracking still works.
            }
        });
    }

    // Install onYouTubeIframeAPIReady hook so initYouTubePlayers runs once the API
    // is available — regardless of which consumer actually injects the script.
    function installApiReadyHook() {
        const previousReady = win.onYouTubeIframeAPIReady;

        win.onYouTubeIframeAPIReady = function() {
            if (typeof previousReady === 'function') {
                previousReady();
            }
            initYouTubePlayers();
        };
    }

    function ensureYouTubeApi() {
        if (win.YT && typeof win.YT.Player === 'function') {
            initYouTubePlayers();
            return;
        }
        if (!doc.querySelector('lite-youtube[videoid], iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"]')) {
            return;
        }
        if (!doc.querySelector('script[src*="youtube.com/iframe_api"]')) {
            const script = doc.createElement('script');
            script.src = 'https://www.youtube.com/iframe_api';
            script.async = true;
            doc.head.appendChild(script);
        }
    }

    function initVideoTracking() {
        installApiReadyHook();

        doc.addEventListener('click', (event) => {
            const videoElement = event.target.closest ? event.target.closest('lite-youtube[videoid], iframe[src*="youtube.com/embed"], iframe[src*="youtube-nocookie.com/embed"]') : null;
            if (!videoElement) {
                return;
            }

            trackVideo(videoElement, 'video_open');
            // First user interaction with any YouTube element: load the API, then
            // attach players once lite-youtube has inserted its iframe.
            ensureYouTubeApi();
            win.setTimeout(initYouTubePlayers, 500);
        }, true);

        if ('MutationObserver' in win) {
            dynamicObserver = new MutationObserver(() => initYouTubePlayers());
            dynamicObserver.observe(doc.body, {
                childList: true,
                subtree: true,
            });
        }
    }

    function initLinkTracking() {
        doc.addEventListener('click', (event) => {
            const link = event.target.closest ? event.target.closest('a[href]') : null;
            if (!link) {
                return;
            }

            const payload = classifyLink(link);
            if (payload) {
                push(EVENTS.linkClick, payload);
            }
        }, true);
    }

    function initViewMoreTracking() {
        doc.addEventListener('show.bs.collapse', (event) => {
            const sectionData = getSectionData(event.target);
            if (!sectionData.content_section) {
                return;
            }

            push(EVENTS.interaction, {
                engagement_type: 'view_more_expand',
                ...sectionData,
            });
        });

        doc.addEventListener('hide.bs.collapse', (event) => {
            const sectionData = getSectionData(event.target);
            if (!sectionData.content_section) {
                return;
            }

            push(EVENTS.interaction, {
                engagement_type: 'view_more_collapse',
                ...sectionData,
            });
        });
    }

    function init() {
        markInferredSections();
        initSectionViews();
        initScrollTracking();
        initLinkTracking();
        initVideoTracking();
        initViewMoreTracking();
    }

    const api = {
        push,
        trackInteraction(params = {}) {
            return push(EVENTS.interaction, params);
        },
        trackLinkClick(params = {}) {
            return push(EVENTS.linkClick, params);
        },
        trackVideo(params = {}) {
            return push(EVENTS.video, params);
        },
        refresh() {
            markInferredSections();
            initYouTubePlayers();
        },
        classifyLink,
    };

    win.coeUndergradDegreeAnalytics = api;

    if (win.COE_ANALYTICS_TEST) {
        api._test = {
            classifyLink,
            inferLinkData,
            getAdmissionType,
            normalizeText,
            getSectionData,
        };
    }

    if (doc.readyState === 'loading') {
        doc.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
